/**
 * This utility file provides functions to process and transform raw materialized table data into a structured format
 * suitable for use in the table definition editor. It handles:
 * - Mapping raw table rows and headers to processed cells with additional metadata (e.g., overrides, evaluation
 * details).
 * - Filtering out unnecessary property and unit-of-measure (UOM) columns from the processed data.
 * - Processing individual cell types (e.g., numeric, UUID, boolean) based on their column definitions.
 * - Adding evaluation details and override indicators to cells when applicable.
 */
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import type {
  BooleanTableCell,
  ColumnDefinition,
  EditorType,
  ItemTableCell,
  MaterializedTableCell,
  MaterializedTableHeader,
  MaterializedTableOutput,
  MaterializedTableRow,
  NumericTableCell,
  ProcessedMaterializedTable,
  ProcessedTableCell,
  TextTableCell,
} from '@/tableDefinitionEditor/tableDefinition.types';
import { PROPERTY_COLUMN_MATCH_SEGMENT, UOM_COLUMN_MATCH_SUFFIX } from '@/tableDefinitionEditor/tableDefinition.types';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { sqScalingTableDefinitionStore } from '@/core/core.stores';
import _ from 'lodash';
import { toNumber } from '@/utilities/numberHelper.utilities';
import { CSV_TYPES } from './tableDefinition.constants';
import { getTableDefinitionStore } from '@/tableDefinitionEditor/store.utilities';

/* Filter out property columns and uom columns from headers and rows so we do not put extra data in the store */
export const filterPropertyAndUomColumns = (
  headers: MaterializedTableHeader[],
  processedRows: ProcessedTableCell[][],
  hasMore: boolean,
) => {
  const propertyAndUomColumnIndices = headers
    .map((header, index) => ({ name: header.name, index }))
    .filter(({ name }) => name.includes(PROPERTY_COLUMN_MATCH_SEGMENT) || name.includes(UOM_COLUMN_MATCH_SUFFIX))
    .map(({ index }) => index);
  const filteredHeaders = headers.filter((_, index) => !propertyAndUomColumnIndices.includes(index));
  const filteredRows = processedRows.map((row) =>
    row.filter((_, index) => !propertyAndUomColumnIndices.includes(index)),
  );

  return {
    headers: filteredHeaders,
    rows: filteredRows,
    hasMore,
  };
};

const overrideIndicator = (
  row: MaterializedTableRow,
  headers: MaterializedTableHeader[],
  column: ColumnDefinition | undefined,
) => {
  const overrideColumnIndex = headers.findIndex((header) =>
    header.name.endsWith(`."${column?.id?.toLowerCase()}_overrides"`),
  );
  const overrideValue = overrideColumnIndex > -1 ? row[overrideColumnIndex] : undefined;
  return overrideValue !== undefined && overrideValue !== null;
};

const evaluationRuleResults = (
  row: MaterializedTableRow,
  headers: MaterializedTableHeader[],
  column: ColumnDefinition | undefined,
) => {
  const itemIdIndex = headers.findIndex((header) => header.name === SeeqNames.MaterializedTables.ItemIdColumn);
  const datumIdIndex = headers.findIndex((header) => header.name === SeeqNames.MaterializedTables.DatumIdColumn);

  const evaluationDetails = column?.evaluationDetails?.find(
    (evaluationDetail) =>
      evaluationDetail.itemId.toUpperCase() === row[itemIdIndex]?.toString().toUpperCase() &&
      (!row[datumIdIndex] || evaluationDetail.datumId?.toUpperCase() === row[datumIdIndex]?.toString().toUpperCase()),
  );
  return evaluationDetails?.ruleResults;
};

const processDefaultCell = (cellValue: MaterializedTableCell) => {
  return {
    value: cellValue ? cellValue.toString() : undefined,
  } as TextTableCell;
};

const processNumericCell = ({
  cellValue,
  row,
  header,
  headers,
  column,
}: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  header: MaterializedTableHeader;
  headers: MaterializedTableHeader[];
  column: ColumnDefinition | undefined;
}) => {
  const headerMap = headers.reduce((map, header) => {
    map.set(header.name, header);
    return map;
  }, new Map<string, MaterializedTableHeader>());

  const potentialNumericValue = toNumber(cellValue);
  const potentialUomColumn = headerMap.get(`${header.name}${UOM_COLUMN_MATCH_SUFFIX}`);
  const potentialUomColumnIndex = headers.findIndex((h) => h.name === potentialUomColumn?.name);
  const potentialUomColumnValue = potentialUomColumnIndex > -1 ? row[potentialUomColumnIndex] : undefined;
  // If there was not a uom column, then check the column definition for a uom override
  const uomColumnValue = potentialUomColumnValue ?? column?.columnUom;

  if (_.isNil(potentialNumericValue)) {
    return processDefaultCell(cellValue);
  } else {
    return {
      value: potentialNumericValue,
      uom: uomColumnValue as string | undefined,
    } as Partial<NumericTableCell>;
  }
};

const processUuidCell = ({
  cellValue,
  row,
  header,
  headers,
  column,
}: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  header: MaterializedTableHeader;
  headers: MaterializedTableHeader[];
  column: ColumnDefinition | undefined;
}) => {
  const potentialPropertyColumns = [
    ...(column?.additionalProperties || []),
    SeeqNames.Properties.IsModifiedOutsideTable,
    SeeqNames.Properties.Type,
    SeeqNames.Properties.Name,
  ];

  const potentialPropertyToDisplay = column?.displayProperty;
  if (potentialPropertyToDisplay) {
    potentialPropertyColumns.push(potentialPropertyToDisplay);
  }

  const getPropertyValue = (property: string) => {
    const propertyToDisplayHeaderIndex = headers.findIndex(
      (propertyHeader) => propertyHeader.name === `${header.name}${PROPERTY_COLUMN_MATCH_SEGMENT}${property}`,
    );
    return propertyToDisplayHeaderIndex > -1 ? (row[propertyToDisplayHeaderIndex] as string) : undefined;
  };

  const fetchedProperties = potentialPropertyColumns.reduce((props, property) => {
    props[property] = getPropertyValue(property);
    return props;
  }, {} as Record<string, string | undefined>);

  // If the value is the same as the table definition id, then it is a not found item from CSV import
  const id =
    cellValue === sqScalingTableDefinitionStore.id
      ? { tableDefinitionId: cellValue as string }
      : { uuid: cellValue as string };

  return {
    ...id,
    fetchedProperties,
    isModifiedOutsideTable: fetchedProperties[SeeqNames.Properties.IsModifiedOutsideTable],
  } as Partial<ItemTableCell>;
};

const processBooleanCell = (cellValue: MaterializedTableCell) => {
  return {
    value: !!cellValue,
  } as Partial<BooleanTableCell>;
};

const processCellByType = (processCellProps: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  header: MaterializedTableHeader;
  headers: MaterializedTableHeader[];
  column: ColumnDefinition | undefined;
}) => {
  if (_.isNil(processCellProps.cellValue)) {
    return {
      value: undefined,
    };
  }

  switch (processCellProps.header.type) {
    case ColumnTypeEnum.NUMERIC:
      return processNumericCell(processCellProps);
    case ColumnTypeEnum.UUID:
      return processUuidCell(processCellProps);
    case ColumnTypeEnum.BOOLEAN:
      return processBooleanCell(processCellProps.cellValue);
    default: {
      return processDefaultCell(processCellProps.cellValue);
    }
  }
};

const mapRowToProcessedCells = (
  row: MaterializedTableRow,
  headers: MaterializedTableHeader[],
  columns: ColumnDefinition[],
): ProcessedTableCell[] => {
  return row.map((cellValue, columnIndex) => {
    const header = headers[columnIndex];
    const column = columns.find((col) => col.columnName === header.name);

    return {
      override: overrideIndicator(row, headers, column),
      evaluationDetails: evaluationRuleResults(row, headers, column),
      ...processCellByType({ cellValue, row, header, headers, column }),
    };
  });
};

export const processMaterializedTable = (
  rawMaterializedTable: MaterializedTableOutput | undefined,
  columns: ColumnDefinition[],
): ProcessedMaterializedTable | undefined => {
  if (!rawMaterializedTable) {
    return undefined;
  }
  const rows = rawMaterializedTable.rows;
  const headers = rawMaterializedTable.headers;

  const processedRows: ProcessedTableCell[][] = rows.map((row) => {
    return mapRowToProcessedCells(row, headers, columns);
  });

  return filterPropertyAndUomColumns(headers, processedRows, rawMaterializedTable.hasMore);
};

export const getRowInTableByItemId = (itemIdColumnValue: string, editorType: EditorType) => {
  const tableDefinitionStore = getTableDefinitionStore(editorType);
  const materializedTable = tableDefinitionStore.materializedTable;
  const tableType = tableDefinitionStore.tableType;
  const columnHeaderToSearch = CSV_TYPES.includes(tableType)
    ? SeeqNames.MaterializedTables.CsvItemIdColumn
    : SeeqNames.MaterializedTables.ItemIdColumn;

  const headerIndex = materializedTable?.headers.findIndex((header) => header.name === columnHeaderToSearch);
  if (headerIndex === undefined || headerIndex < 0) {
    return undefined;
  }

  return materializedTable?.rows?.find((row) => {
    const itemIdColumnCellToCheck = row[headerIndex] as ItemTableCell;
    return itemIdColumnCellToCheck && itemIdColumnCellToCheck.uuid === itemIdColumnValue;
  });
};
